import { EndpointKeys } from '../config/api';
import ApiService from './api';
import { 
  ContractorAgreementFormData, 
  CreateContractorContractResponse 
} from '../types/contractor-agreement';

export interface Contractor {
  id: number;
  email: string;
  status: string;
  lastActive: string;
  totalContracts: number;
  activeContracts: number;
  completedContracts: number;
}

export interface CreateContractorRequest {
  email: string;
  password: string;
}

export const contractorService = {
  async listContractors(): Promise<Contractor[]> {
    const response = await ApiService.request<{ contractors: Contractor[] }>(
      EndpointKeys.OPERATIONS_CONTRACTORS
    );
    return response.contractors;
  },

  async createContractor(data: CreateContractorRequest): Promise<Contractor> {
    return ApiService.request<Contractor>(
      EndpointKeys.OPERATIONS_CREATE_CONTRACTOR,
      {
        method: 'POST',
        body: data
      }
    );
  },

  async updateContract(
    contractId: string, 
    agreementData: ContractorAgreementFormData, 
    regeneratePdf?: boolean
  ): Promise<CreateContractorContractResponse> {
    return ApiService.request<CreateContractorContractResponse>(
      EndpointKeys.UPDATE_CONTRACTOR_CONTRACT,
      {
        method: 'PUT',
        body: {
          agreementData,
          regeneratePdf: regeneratePdf || false
        },
        urlParams: { contractId }
      }
    );
  },

  // Stripe OAuth methods
  async initiateStripeConnection(): Promise<{ oauth_url: string }> {
    return ApiService.request<{ oauth_url: string }>(
      EndpointKeys.STRIPE_OAUTH_CONNECT,
      {
        method: 'POST'
      }
    );
  },

  async handleStripeCallback(code: string, state: string): Promise<{
    connected: boolean;
    account_id?: string;
    charges_enabled?: boolean;
    payouts_enabled?: boolean;
  }> {
    return ApiService.request<{
      connected: boolean;
      account_id?: string;
      charges_enabled?: boolean;
      payouts_enabled?: boolean;
    }>(
      EndpointKeys.STRIPE_OAUTH_CALLBACK,
      {
        method: 'POST',
        body: {
          code,
          state,
        }
      }
    );
  },

  async disconnectStripeAccount(): Promise<{ disconnected: boolean }> {
    return ApiService.request<{ disconnected: boolean }>(
      EndpointKeys.STRIPE_DISCONNECT,
      {
        method: 'DELETE'
      }
    );
  },

  async getStripeConnectionStatus(): Promise<{
    connected: boolean;
    account_id?: string;
    charges_enabled?: boolean;
    payouts_enabled?: boolean;
    details_submitted?: boolean;
    currency?: string;
    capabilities?: Record<string, any>;
  }> {
    return ApiService.request<{
      connected: boolean;
      account_id?: string;
      charges_enabled?: boolean;
      payouts_enabled?: boolean;
      details_submitted?: boolean;
      currency?: string;
      capabilities?: Record<string, any>;
    }>(EndpointKeys.STRIPE_STATUS);
  }
}; 